<template>
  <div class="onboarding-page bg-auth border-top border-top-2 border-primary">
    <div class="onboarding-container container">
      <div class="row onboarding-row align-items-stretch">
        <div class="col-12 col-md-6 offset-md-1 order-md-2 onboarding-form-col">
          <!-- Heading -->
          <h2 class="text-center mb-1">
            Welcome!
          </h2>
          <!-- Subheading -->
          <p class="text-muted text-center mb-3" style="font-size:0.9rem;">
            Fill out the form below to kick-start the onboarding process.
          </p>

          <!-- Step Progress -->
          <div class="d-flex justify-content-center mb-2">
            <div class="d-flex align-items-center">
              <!-- Step 1: Personal Info -->
              <div class="step-circle" :class="{ active: currentStep >= 1, completed: currentStep > 1 }">
                <i class="fe fe-user" v-if="currentStep === 1"></i>
                <i class="fe fe-check" v-else-if="currentStep > 1"></i>
                <span v-else>1</span>
              </div>
              <div class="step-line" :class="{ active: currentStep > 1 }"></div>
              <!-- Step 2: SKU Selection -->
              <div class="step-circle" :class="{ active: currentStep >= 2, completed: currentStep > 2 }">
                <i class="fe fe-cloud" v-if="currentStep === 2"></i>
                <i class="fe fe-check" v-else-if="currentStep > 2"></i>
                <span v-else>2</span>
              </div>
              <div class="step-line" :class="{ active: currentStep > 2 }"></div>
              <!-- Step 3: Configuration (completed early in 4-step flow once CFM section is shown) -->
              <div class="step-circle" :class="{
                active: currentStep === 3 && (!isFourStepFlow || !showCfmResumeFlow),
                completed: currentStep > 3 || (currentStep === 3 && isFourStepFlow && showCfmResumeFlow)
              }">
                <i class="fe fe-settings" v-if="currentStep === 3 && (!isFourStepFlow || !showCfmResumeFlow)"></i>
                <i class="fe fe-check" v-else-if="currentStep > 3 || (currentStep === 3 && isFourStepFlow && showCfmResumeFlow)"></i>
                <span v-else>3</span>
              </div>
              <!-- Step 4: CloudFormation Output (only for CFM flows) -->
              <template v-if="isFourStepFlow">
                <div class="step-line" :class="{ active: currentStep === 3 && showCfmResumeFlow }"></div>
                <div class="step-circle" :class="{
                  active: currentStep === 3 && showCfmResumeFlow,
                  completed: currentStep > 3
                }">
                  <i class="fe fe-clipboard" v-if="currentStep === 3 && showCfmResumeFlow"></i>
                  <i class="fe fe-check" v-else-if="currentStep > 3"></i>
                  <span v-else>4</span>
                </div>
              </template>
            </div>
          </div>

          <!-- Step Labels -->
          <div class="d-flex justify-content-center mb-3">
            <div class="row text-center w-100">
              <div :class="isFourStepFlow ? 'col-3' : 'col-4'">
                <small class="text-muted">Personal Info</small>
              </div>
              <div :class="isFourStepFlow ? 'col-3' : 'col-4'">
                <small class="text-muted">SKU {{editable?"Selection":"Selected"}}</small>
              </div>
              <div :class="isFourStepFlow ? 'col-3' : 'col-4'">
                <small class="text-muted">Configuration</small>
              </div>
              <div v-if="isFourStepFlow" class="col-3">
                <small class="text-muted">CloudFormation</small>
              </div>
            </div>
          </div>

          <!-- Error Messages -->
          <div v-if="errorMessage" class="alert alert-danger mb-2 py-2">
            {{ errorMessage }}
          </div>

          <!-- Loading State -->
          <div v-if="isSubmitting" class="text-center">
            <div class="mb-4">
              <i class="fe fe-loader spin text-primary" style="font-size: 4rem;"></i>
            </div>
            <h3 class="mb-3">Processing Your Request...</h3>
            <p class="text-muted mb-4">
              Please wait while we set up your account. This may take a few moments.
            </p>
            <div class="progress" style="height: 4px;">
              <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar"
                style="width: 100%">
              </div>
            </div>
          </div>

          <!-- Success State -->
          <div v-else-if="isSubmitted" class="text-center">
            <div class="mb-4">
              <i class="fe fe-check-circle text-success" style="font-size: 4rem;"></i>
            </div>
            <h3 class="mb-3">Thank You!</h3>
            <p class="text-muted mb-4">
              Your onboarding request has been successfully submitted. You will receive an email notification
              when the automated onboarding and deployment process is completed.
            </p>
            <p v-if="!existCust" class="text-muted mb-4">
              Once you receive the confirmation email, you'll be able to log in with your provided email address
              (<strong>{{ formData.email }}</strong>) to access your account.
            </p>
            <a href="/login" class="btn btn-primary btn-lg">
              <i class="fe fe-log-in"></i> Go to Login Page
            </a>
          </div>

          <!-- Form -->
          <form v-else @submit.prevent="submitForm">

            <!-- Step 1: Personal Information -->
            <div v-if="currentStep === 1" class="step-content">
              <h6 class="mb-3 text-muted fw-semibold text-uppercase" style="font-size:0.72rem;letter-spacing:.08em;">Step 1 — Personal Information</h6>

              <div class="form-group">
                <InputField type="radio" name="existingCustomer" label="Existing Customer" :inlineField="true"
                  toolTip="Are you an existing customer?" :options="existingCustOption" v-model="existCust" />
              </div>
              <div class="form-group">
                <InputField v-if="existCust" type="text" name="cid" label="Customer ID (CID)" placeholder="eg: c001"
                  toolTip="Enter already registered CID eg:c001" v-model="cid"
                  :validate="[{ type: 'required', errMsg: 'cid is required' }]" />
              </div>
              <div class="form-group">
                <InputField type="text" name="enail" label="Organization Name" placeholder="Your Organization Name"
                  :toolTip='existCust ? "Enter the registered organization name." : "Enter the organization name"'
                  v-model="orgName" :validate="[{ type: 'required', errMsg: 'organization name is required' }]" />
              </div>
              <!-- Email address (existing customers already have an email on file) -->
              <div v-if="!existCust" class="form-group">
                <InputField type="text" name="enail" label="Email Address" placeholder="name@address.com"
                  toolTip="Enter a valid email address. This will be used for account login and notifications."
                  v-model="formData.email" :validate="[{ type: 'required', errMsg: 'email is required' },
                  { type: 'regex', pattern: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$', errMsg: 'enter valid email address' }
                  ]" />
              </div>
            </div>

            <!-- Step 2: SKU Selection -->
            <div v-if="currentStep === 2" class="step-content mb-3">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="mb-0 text-muted fw-semibold text-uppercase" style="font-size:0.72rem;letter-spacing:.08em;">Step 2 — SKU {{editable?"Selection":"Selected"}}</h6>
                <a href="https://help.aqfer.net/en_US/aqfer-onboarding-cloud-services/onboarding-guidelines" target="_blank" rel="noopener" class="small text-primary text-nowrap">
                  <i class="fe fe-help-circle me-1"></i>View onboarding guidelines
                </a>
              </div>

              <!-- SKU Selection -->
              <div class="form-group">
                <InputField type="select" name="sku" label="SKU" :options="skuOptions" v-model="formData.sku"
                  :editable="editable" />
              </div>

              <!-- Checking SKU status indicator for existing customers -->
              <div v-if="existCust && formData.sku && checkingSkuStatus" class="d-flex align-items-center text-muted small mb-2">
                <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                Checking SKU registration status...
              </div>
              <!-- Resume banner when existing customer has AWAITING_CLIENT_INPUT SKU -->
              <div v-if="existCust && formData.sku && isResumingFlow && !checkingSkuStatus" class="resume-banner mb-2">
                <div class="resume-banner-icon"><i class="fe fe-refresh-cw"></i></div>
                <div class="resume-banner-body">
                  <strong>Resume Detected</strong>
                  <p class="mb-0 mt-1 small">
                    <strong>{{ formData.sku }}</strong> is registered but CloudFormation deployment is pending.
                    Proceed to the next step to view the template and paste your cfm output.
                  </p>
                </div>
              </div>

              <!-- SKU-specific Help Links -->
              <div v-if="skuHelpjuiceLinks.length" class="mb-2 small text-info">
                <i class="fe fe-info me-1"></i>
                <a v-if="skuHelpjuiceLinks.length === 1" :href="skuHelpjuiceLinks[0]" target="_blank" rel="noopener">
                  Learn more about {{ formData.sku }} setup and features
                </a>
                <template v-else>
                  Learn more about {{ formData.sku }} setup and features:
                  <template v-for="(link, idx) in skuHelpjuiceLinks" :key="link">
                    <a :href="link" target="_blank" rel="noopener">Link {{ idx + 1 }}</a><span v-if="idx < skuHelpjuiceLinks.length - 1">, </span>
                  </template>
                </template>
              </div>

              <!-- SKU Features Display -->
              <div v-if="formData.sku">
                <div class="card border-0 bg-light mb-2">
                  <div class="card-body py-2 px-3">
                    <div class="small fw-semibold mb-1">{{ formData.sku }} Features</div>
                    <div class="small" v-html="features"></div>
                  </div>
                </div>
              </div>

              <!-- SKU Configuration Fields -->
              <div v-if="skuFields.length > 0 && !isResumingFlow && !isInProgress && !isClientInputReceived" class="sku-config-fields mt-3"
                :class="{ 'two-columns': skuFields.length > 3 }">
                <FormFields :fields="skuFields" v-model="formData.sku_info" />
              </div>
            </div>

            <!-- Step 3: SKU Configuration -->
            <div v-if="currentStep === 3" class="step-content mb-3">
              <div class="d-flex align-items-center justify-content-between mb-3">
                <h6 class="mb-0 text-muted fw-semibold text-uppercase" style="font-size:0.72rem;letter-spacing:.08em;">Step 3 — {{ noProduct ? 'Account Created' : (noTemplate ? 'Confirm & Start Onboarding' : 'CloudFormation Setup') }}</h6>
                <!-- SKU badge inline with heading -->
                <span v-if="formData.sku" class="sku-badge">
                  <i class="fe fe-package me-1"></i>{{ formData.sku }}
                </span>
              </div>

              <!-- Resume flow banner for existing customer (AWAITING_CLIENT_INPUT) -->
              <div v-if="isResumingFlow" class="resume-banner mb-3">
                <div class="resume-banner-icon"><i class="fe fe-refresh-cw"></i></div>
                <div class="resume-banner-body">
                  <strong>Resuming Onboarding</strong>
                  <p class="mb-0 mt-1 small">
                    CID <code>{{ cid }}</code> has a pending CloudFormation deployment for
                    <strong>{{ formData.sku }}</strong>. View the template below, then paste
                    your cfm output to continue.
                  </p>
                </div>
              </div>

              <!-- IN_PROGRESS banner: onboarding already triggered and running -->
              <div v-if="isInProgress" class="in-progress-banner mb-3">
                <div class="in-progress-banner-icon"><i class="fe fe-clock"></i></div>
                <div class="in-progress-banner-body">
                  <strong>Onboarding In Progress</strong>
                  <p class="mb-0 mt-1 small">
                    <strong>{{ formData.sku }}</strong> is already being onboarded for CID
                    <code>{{ cid }}</code>. You will receive an email when the process completes.
                    No further action is required.
                  </p>
                </div>
              </div>

              <!-- CLIENT_INPUT_RECEIVED banner: cfm output already submitted, codebuild not triggered -->
              <div v-if="isClientInputReceived" class="cir-banner mb-3">
                <div class="cir-banner-icon"><i class="fe fe-alert-circle"></i></div>
                <div class="cir-banner-body">
                  <strong>Deployment Not Yet Started</strong>
                  <p class="mb-0 mt-1 small">
                    CloudFormation output was received for <strong>{{ formData.sku }}</strong>
                    but the deployment hasn't been triggered yet. Click
                    <strong>Re-trigger Onboarding</strong> below to start the process.
                  </p>
                </div>
              </div>

              <!-- Created CID note (compact, new registration) -->
              <div v-if="createdCid" class="cid-note mb-3">
                <i class="fe fe-check-circle cid-note-icon"></i>
                <span class="cid-note-label">CID Created</span>
                <code class="cid-note-value">{{ createdCid }}</code>
                <button type="button" class="btn btn-xs cid-note-copy" @click="copyCreatedCid">
                  <i class="fe fe-copy me-1"></i>Copy
                </button>
              </div>

              <!-- Instructions Accordion - shown for no-CFM SKUs always, and for CFM SKUs only after Create CID or in resume flow -->
              <div v-if="formData.sku && !noTemplate && (!hasCfm || showCfmResumeFlow)" class="accordion mb-3" id="instructionsAccordion">
                <div class="accordion-item">
                  <h2 class="accordion-header" id="instructionsHeading">
                    <button class="accordion-button" type="button" data-bs-toggle="collapse"
                      data-bs-target="#instructionsCollapse" aria-expanded="true" aria-controls="instructionsCollapse">
                      <i class="fe fe-book-open me-2"></i>Setup Instructions
                    </button>
                  </h2>
                  <div id="instructionsCollapse" class="accordion-collapse collapse show"
                    aria-labelledby="instructionsHeading" data-bs-parent="#instructionsAccordion">
                    <div class="accordion-body">
                      <div class="alert alert-info-bg mb-0">
                        <div v-html="instructions"></div>
                        <div v-if="templateLoading" class="d-flex align-items-center gap-2 mt-2">
                          <span class="spinner-border spinner-border-sm" role="status"></span>
                          <span class="small text-muted">Loading CloudFormation template…</span>
                        </div>
                        <!-- CFM template link stays available here; full copy is also emailed to the client -->
                        <div v-if="templateUrl" class="cfm-template-actions mt-3 pt-3 border-top">
                          <i class="fe fe-mail me-2 text-muted"></i>
                          <span class="small">
                            The client will receive an email with the
                            <a :href="templateUrl" target="_blank" rel="noopener">CloudFormation template</a><template v-if="skuHelpjuiceLinks.length === 1"> and the
                            <a :href="skuHelpjuiceLinks[0]" target="_blank" rel="noopener">{{ formData.sku }} SKU documentation</a></template><template v-else-if="skuHelpjuiceLinks.length > 1"> and the {{ formData.sku }} SKU documentation
                            (<template v-for="(link, idx) in skuHelpjuiceLinks" :key="link"><a :href="link" target="_blank" rel="noopener">Link {{ idx + 1 }}</a><span v-if="idx < skuHelpjuiceLinks.length - 1">, </span></template>)</template>
                            — client need to deploy the template in their AWS account to complete setup.
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- No automated product for this SKU -->
              <div v-else-if="noProduct && createdCid" class="info-banner mb-3">
                <div class="info-banner-icon"><i class="fe fe-info"></i></div>
                <div class="info-banner-body">
                  <strong>No Automation Available</strong>
                  <p class="mb-0 mt-1 small">
                    There's no automated setup for the <span class="sku-chip">{{ formData.sku }}</span> SKU.
                    Client account has been created and a confirmation email has been sent — no further action is required.
                  </p>
                </div>
              </div>

              <!-- No CFM notice -->
              <div v-else-if="noTemplate && !isInProgress" class="info-banner mb-3">
                <div class="info-banner-icon"><i class="fe fe-info"></i></div>
                <div class="info-banner-body">
                  <strong>No CloudFormation Template Required</strong>
                  <p class="mb-0 mt-1 small">
                    The <span class="sku-chip">{{ formData.sku }}</span> SKU doesn't require a CloudFormation template.
                  </p>
                </div>
              </div>

              <!-- CFM Output section - shown after Create CID or for resume flow -->
              <div v-if="showCfmResumeFlow" class="cfm-output-card mt-3">

                <!-- Card header with mode toggle -->
                <div class="cfm-output-card-header">
                  <i class="fe fe-terminal me-2"></i>
                  CloudFormation Output
                  <span class="text-danger ms-1">*</span>
                  <!-- Mode toggle — hidden while fields are loading or if no fields returned -->
                  <div v-if="!cfmFieldsLoading && cfmOutputFields.length > 0"
                       class="cfm-mode-toggle ms-auto">
                    <button type="button"
                      :class="['cfm-mode-btn', cfmInputMode === 'fill' ? 'active' : '']"
                      @click="cfmInputMode = 'fill'">
                      <i class="fe fe-edit-2 me-1"></i>Fill Fields
                    </button>
                    <button type="button"
                      :class="['cfm-mode-btn', cfmInputMode === 'paste' ? 'active' : '']"
                      @click="cfmInputMode = 'paste'">
                      <i class="fe fe-clipboard me-1"></i>Paste JSON
                    </button>
                  </div>
                  <span v-else-if="cfmFieldsLoading" class="cfm-output-hint ms-auto">
                    <span class="spinner-border spinner-border-sm me-1"></span>Loading fields…
                  </span>
                  <span v-else class="cfm-output-hint ms-auto">
                    JSON · AWS CloudFormation &rsaquo; Stacks &rsaquo; Outputs
                  </span>
                </div>

                <!-- FILL MODE -->
                <div v-if="cfmInputMode === 'fill' && cfmOutputFields.length > 0"
                     class="cfm-fill-body">
                  <div v-for="field in cfmOutputFields" :key="field.key" class="cfm-field-row"
                       :class="{ 'cfm-field-missing': missingCfmFields.includes(field.key) }">
                    <div class="cfm-field-label">
                      <span>{{ field.key }}</span>
                      <span v-if="field.required !== false" class="text-danger ms-1">*</span>
                      <span v-if="field.description" class="cfm-field-desc">{{ field.description }}</span>
                    </div>
                    <input
                      class="cfm-field-input"
                      type="text"
                      :placeholder="`Enter ${field.key}`"
                      v-model="cfmOutputFieldValues[field.key]"
                      @input="syncFieldsToJson"
                    />
                    <i v-if="cfmOutputFieldValues[field.key]?.trim()"
                       class="fe fe-check-circle cfm-field-check"></i>
                  </div>

                  <!-- Missing fields summary -->
                  <div v-if="missingCfmFields.length > 0" class="cfm-missing-bar">
                    <i class="fe fe-alert-circle me-2"></i>
                    Required: <strong>{{ missingCfmFields.join(', ') }}</strong>
                  </div>
                  <div v-else class="cfm-output-valid">
                    <i class="fe fe-check-circle me-1"></i>All fields filled — ready to submit
                  </div>
                </div>

                <!-- PASTE MODE -->
                <div v-else class="cfm-output-card-body">
                  <textarea
                    class="cfm-output-textarea"
                    rows="6"
                    v-model="cfmOutput"
                    @input="syncJsonToFields"
                    placeholder='[&#10;  { "OutputKey": "BucketName", "OutputValue": "my-bucket" },&#10;  { "OutputKey": "RoleArn",    "OutputValue": "arn:aws:iam::..." }&#10;]'
                    :class="{ 'has-error': cfmOutputError }"
                  ></textarea>
                  <div v-if="cfmOutputError" class="cfm-output-error">
                    <i class="fe fe-alert-circle me-2 flex-shrink-0 mt-1"></i>
                    <div>
                      <div v-for="(line, i) in cfmOutputError.split('\n')" :key="i">
                        {{ line.replace(/^\d+:\s*/, '').trim() }}
                      </div>
                    </div>
                  </div>
                  <div v-else-if="cfmOutputValid" class="cfm-output-valid">
                    <i class="fe fe-check-circle me-1"></i>JSON detected — ready to submit
                  </div>
                </div>

              </div>
            </div>

            <!-- Navigation Buttons -->
            <div class="d-flex justify-content-between mt-2 mb-0">
              <button v-if="currentStep > 1" type="button" class="btn btn-outline-secondary" @click="previousStep">
                <i class="fe fe-arrow-left"></i> Previous
              </button>
              <div v-else></div>

              <!-- Step 1: Next button -->
              <button v-if="currentStep === 1" type="button" class="btn btn-primary" @click="nextStep"
                :disabled="!canProceedToNextStep">
                Next <i class="fe fe-arrow-right"></i>
              </button>

              <!-- Step 2: Create CID (no-CFM) or Next (CFM flow) -->
              <template v-else-if="currentStep === 2">
                <!-- No automated product: create the CID, send welcome email, advance -->
                <button
                  v-if="noProduct && !checkingSkuStatus && formData.sku && !isResumingFlow && !isInProgress && !isClientInputReceived"
                  type="button"
                  class="btn btn-primary"
                  @click="createCidNoProduct"
                  :disabled="!canProceedToNextStep || isCreatingCid"
                >
                  <span v-if="isCreatingCid">
                    <span class="spinner-border spinner-border-sm me-1" role="status"></span>Creating CID...
                  </span>
                  <span v-else>Create CID <i class="fe fe-arrow-right"></i></span>
                </button>

                <!-- No CFM after check: create the CID, then advance to the confirmation step -->
                <button
                  v-else-if="noTemplate && !checkingSkuStatus && !templateLoading && formData.sku && !isResumingFlow && !isInProgress && !isClientInputReceived"
                  type="button"
                  class="btn btn-primary"
                  @click="createCidNoCfm"
                  :disabled="!canProceedToNextStep || isCreatingCid"
                >
                  <span v-if="isCreatingCid">
                    <span class="spinner-border spinner-border-sm me-1" role="status"></span>Creating CID...
                  </span>
                  <span v-else>Create CID <i class="fe fe-arrow-right"></i></span>
                </button>

                <!-- CFM flow or still checking: Next (calls API then advances) -->
                <button v-else type="button" class="btn btn-primary" @click="handleStep2Next"
                  :disabled="!canProceedToNextStep || checkingSkuStatus || templateLoading || isCreatingCid">
                  <span v-if="isCreatingCid">
                    <span class="spinner-border spinner-border-sm me-1" role="status"></span>Creating CID...
                  </span>
                  <span v-else-if="checkingSkuStatus || templateLoading">
                    <span class="spinner-border spinner-border-sm me-1" role="status"></span>Checking...
                  </span>
                  <span v-else>Next <i class="fe fe-arrow-right"></i></span>
                </button>
              </template>

              <!-- Step 3 buttons -->
              <template v-else>
                <!-- IN_PROGRESS: no action needed -->
                <span v-if="isInProgress"></span>

                <!-- CLIENT_INPUT_RECEIVED: re-trigger onboarding to start codebuild -->
                <button
                  v-else-if="isClientInputReceived"
                  type="button"
                  class="btn btn-warning"
                  @click="retriggerOnboarding"
                  :disabled="isCreatingCid"
                >
                  <span v-if="isCreatingCid">
                    <span class="spinner-border spinner-border-sm me-1" role="status"></span>Triggering...
                  </span>
                  <span v-else><i class="fe fe-zap me-1"></i>Re-trigger Onboarding</span>
                </button>

                <!-- Start Onboarding after CFM output -->
                <button
                  v-else-if="showCfmResumeFlow"
                  type="button"
                  class="btn btn-success"
                  @click="startOnboardingWithCfm"
                  :disabled="!cfmOutputValid || isSubmitting"
                >
                  <span v-if="isSubmitting">
                    <span class="spinner-border spinner-border-sm me-1" role="status"></span>Processing...
                  </span>
                  <span v-else><i class="fe fe-check me-1"></i>Start Onboarding</span>
                </button>

                <!-- No automated product: CID created and welcome email already sent, nothing left to do -->
                <span v-else-if="noProduct"></span>

                <!-- No-CFM SKU: CID already created, trigger onboarding + welcome email -->
                <button
                  v-else-if="noTemplate && createdCid"
                  type="button"
                  class="btn btn-success"
                  @click="startOnboardingNoCfm"
                  :disabled="isSubmitting"
                >
                  <span v-if="isSubmitting">
                    <span class="spinner-border spinner-border-sm me-1" role="status"></span>Processing...
                  </span>
                  <span v-else><i class="fe fe-check me-1"></i>Start Onboarding</span>
                </button>
              </template>

            </div>
          </form>
        </div>
        <div class="col-12 col-md-5 order-md-1 onboarding-logo-col d-none d-md-flex">
          <!-- Image -->
          <div class="text-center">
            <img v-if="$store.state.darkMode" src="../assets/logo.png" alt="" class="img-fluid logo-img">
            <img v-else src="../assets/logo2.png" alt="" class="img-fluid logo-img">
          </div>
        </div>
      </div>
    </div>
    </div>
</template>

<script>
import InputField from '../components/InputField.vue';
import FormFields from '../components/FormFields.vue';
import axios from "axios";
import debounce from 'lodash/debounce';
export default {
  name: "Onboarding",
  components: {
    InputField,
    FormFields,
  },
  data() {
    return {
      currentStep: 1,
      isSubmitting: false,
      isSubmitted: false,
      errorMessage: '',
      existCust: false,
      cid: '',
      orgName: '',
      formData: {
        email: '',
        mkt_token: null,
        sku: null,
        sku_info: {}
      },
      skuFields: [],
      existingCustOption: [
        { value: true, label: 'Yes' },
        { value: false, label: 'No' }
      ],
      skuList: [],
      editable: true,
      templateUrl: null,
      templateLoading: false,
      type: 'aqfer',
      noTemplate: false,
      noProduct: false,
      // CFM / CID flow
      createdCid: '',
      cfmOutput: '',
      cfmOutputError: '',
      isCreatingCid: false,
      cfmCheckComplete: false,
      registeredSkuStatus: null,
      registeredSkuData: null,
      checkingSkuStatus: false,
      existingCidOnboardingCalled: false,
      // CFM output fields (from GET /v1/skus/{sku}/cfm_output_fields)
      cfmOutputFields: [],
      cfmOutputFieldValues: {},
      cfmInputMode: 'fill',   // 'fill' | 'paste'
      cfmFieldsLoading: false,
    }
  },
  computed: {
    canProceedToNextStep() {
      var re = new RegExp("^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$");
      if (this.currentStep === 1) {
        if (this.existCust) return !!this.cid;
        return this.formData.email && re.test(this.formData.email);
      } else if (this.currentStep === 2) {
        if (!this.formData.sku) return false;
        if (this.isResumingFlow || this.isInProgress || this.isClientInputReceived) return true;
        // All required SKU fields must have a value
        const requiredFilled = this.skuFields
          .filter(f => f.validation?.some(v => v.type === 'required'))
          .every(f => {
            const val = this.formData.sku_info?.[f.name];
            return val !== null && val !== undefined && String(val).trim() !== '';
          });
        return requiredFilled;
      }
      return true;
    },
    skuOptions() {
      let options = [];
      this.skuList.map(item => {
        options.push({
          label: item.sku,
          value: item.sku
          })
      });
    return options;
    },
    features() {
      let features=this.skuList.find(p => p.sku === this.formData.sku)?.features || []
      features = features.map((feature=>" - "+feature))
      return features.join("<br/>")
    },
    instructions() {
      let instructions=this.skuList.find(p => p.sku === this.formData.sku)?.instructions || []
      instructions = instructions.map((instruction=>" - "+instruction))
      return instructions.join("<br/>")
    },
    // Helpjuice documentation links for the currently selected SKU
    skuHelpjuiceLinks() {
      return this.formData.sku ? this.helpjuiceLinksForSku(this.formData.sku) : [];
    },
    // True when the selected SKU has an associated CFM template
    hasCfm() {
      return !this.noTemplate && this.cfmCheckComplete && !!this.formData.sku;
    },
    // True when existing customer is resuming after Create CID (status AWAITING_CLIENT_INPUT)
    isResumingFlow() {
      return this.existCust && this.registeredSkuStatus === 'AWAITING_CLIENT_INPUT';
    },
    // True when we need to show the CFM output input and Start Onboarding
    showCfmResumeFlow() {
      return (this.hasCfm && !!this.createdCid) ||
             this.isResumingFlow ||
             (this.hasCfm && this.existCust && this.existingCidOnboardingCalled);
    },
    // True when existing customer's CFM output was received but codebuild hasn't started
    isClientInputReceived() {
      return this.existCust && this.registeredSkuStatus === 'CLIENT_INPUT_RECEIVED';
    },
    // True when existing customer's SKU has any status other than the two actionable ones
    isInProgress() {
      return this.existCust &&
             this.registeredSkuStatus !== null &&
             this.registeredSkuStatus !== 'AWAITING_CLIENT_INPUT' &&
             this.registeredSkuStatus !== 'CLIENT_INPUT_RECEIVED';
    },
    // True when the flow has (or had) a CFM step — shows 4-step stepper
    isFourStepFlow() {
      return this.hasCfm || this.isResumingFlow || (!!this.createdCid && !this.noTemplate) ||
             this.existingCidOnboardingCalled || this.isClientInputReceived || this.isInProgress;
    },
    // Fields that still have no value (required ones only)
    missingCfmFields() {
      return this.cfmOutputFields
        .filter(f => f.required !== false && !String(this.cfmOutputFieldValues[f.key] || '').trim())
        .map(f => f.key);
    },
    // True when the CFM output is ready to submit
    cfmOutputValid() {
      if (!this.showCfmResumeFlow) return true;
      if (this.cfmInputMode === 'paste' || this.cfmOutputFields.length === 0) {
        if (!this.cfmOutput?.trim()) return false;
        try { JSON.parse(this.cfmOutput); return true; } catch { return false; }
      }
      return this.missingCfmFields.length === 0;
    },
  },
  mounted() {
    this.checkMarketplaceToken();
    this.initializeTooltips();
    this.fetchSKUList();
  },
  watch: {
    'formData.sku'(newSku, oldSku) {
      if (newSku !== oldSku) {
        // Reset CFM/CID state on SKU change
        this.cfmCheckComplete = false;
        this.createdCid = '';
        this.cfmOutput = '';
        this.cfmOutputError = '';
        this.registeredSkuStatus = null;
        this.registeredSkuData = null;
        this.cfmOutputFields = [];
        this.cfmOutputFieldValues = {};
        this.cfmInputMode = 'fill';
        this.existingCidOnboardingCalled = false;

        let fields = [];
        this.noTemplate = false;
        const initialSkuInfo = {};
        const selectedSku = this.skuList.find(item => item.sku === newSku);

        // SKUs without an automated product have no CFM template and no
        // start-onboarding step — skip the CFM check entirely.
        this.noProduct = !!selectedSku && selectedSku.is_automation_enabled === false;
        if (this.noProduct) {
          this.noTemplate = true;
          this.cfmCheckComplete = true;
        } else {
          this.fetchCFM(null);
        }

        if (selectedSku) {
          (selectedSku.fields || []).map((value, index) => {
            fields.push({
              name: value.name,
              type: value.values? "select" : value.type.toLowerCase(),
              options: !value.values?null: this.getOptions(value.values),
              label: value.name,
              editable: true,
              url: value.type.toLowerCase() == "url" ? "https://onetimesecret.com/" : null,
              toolTip: value.type.toLowerCase() == "url" ? "Do not directly enter your secret here. use the onetimesecret url generated using the 'key'" :
                      value.name === 'bucket_prefix' ? "Enter a valid S3 bucket prefix (3-40 chars, lowercase letters/numbers/hyphens/periods, must start/end with letter/number, no consecutive periods)" :
                      value.description || null,
              validation: value.required ? [{ type: "required" }] : []
            })
            if (value.type.toLowerCase() == 'url') {
              fields[index].type="text";
              fields[index].validation.push({
                type: "regex", pattern: `^https:\\/\\/(?:[a-zA-Z0-9-]+\\.)?onetimesecret\\.com\\/secret\\/(?:[a-zA-Z0-9-]+)*$`,
                errMsg: "Onetimesecret URL is required"
              })
            }
            if (value.name === 'bucket_prefix') {
              fields[index].validation.push({
                type: "regex",
                pattern: "^(?!xn--|sthree-|amzn-s3-demo-)(?!.*\\.\\.)(?!\\d+\\.\\d+\\.\\d+\\.\\d+)[a-z0-9][a-z0-9.-]{1,38}[a-z0-9](?<!-s3alias)(?<!--ol-s3)(?<!\\.mrap)(?<!--x-s3)(?<!--table-s3)$",
                errMsg: "Invalid bucket prefix. Must be 3-40 characters, lowercase letters/numbers/periods/hyphens only, start/end with letter/number, no consecutive periods, cannot be IP address format, and must follow AWS S3 naming rules"
              })
              fields[index].defaultValue ='com.aqfer'
            }
            if (value.name === 'role_arn') {
              fields[index].validation.push({
                type: "regex",
                pattern: "^arn:(?:aws|aws-us-gov|aws-cn):iam::\\d{12}:role\\/(?:[a-zA-Z0-9+=,.@\\-_]+\\/)*[a-zA-Z0-9+=,.@\\-_]+$",
                errMsg: "Invalid Role ARN. Must be in the format: arn:aws:iam::123456789012:role/RoleName"
              });
            }
            const account_id = sessionStorage.getItem('account_id');
             if (account_id && value.name === 'account_id') {
              initialSkuInfo['account_id'] = account_id;
              fields[index] ={ ...fields[index], value: account_id, editable: false };
            }else{
              initialSkuInfo[value.name] = fields[index].defaultValue !== undefined ? fields[index].defaultValue : '';
            }
          })
          this.skuFields = fields;
          this.formData.sku_info = initialSkuInfo;
        } else {
          this.skuFields = [];
          this.formData.sku_info = {};
        }

        // For existing customers, check if this SKU was already registered
        if (this.existCust && this.cid && newSku) {
          this.checkRegisteredSku();
        }
      }
    },
    type(){
      this.fetchSKUList();
    },
    cid(newVal, oldVal) {
      if (newVal !== oldVal && this.existCust) {
        // Reset all CFM/status state so the new CID's status is checked fresh
        this.registeredSkuStatus = null;
        this.registeredSkuData = null;
        this.existingCidOnboardingCalled = false;
        this.createdCid = '';
        this.cfmOutput = '';
        this.cfmOutputError = '';
        this.cfmOutputFields = [];
        this.cfmOutputFieldValues = {};
        this.cfmInputMode = 'fill';
        if (this.formData.sku) {
          this.checkRegisteredSku();
        }
      }
    },
    showCfmResumeFlow(newVal) {
      if (newVal && !this.cfmFieldsLoading && this.cfmOutputFields.length === 0) {
        this.fetchCfmOutputFields();
      }
    },
    'formData.sku_info.region'(newRegion, oldRegion) {
      // Skip CFM fetch for resume flow (CFM was already deployed)
      if (newRegion != "" && newRegion !== oldRegion && !this.registeredSkuStatus) {
        this.debouncedFetchCFM(newRegion);
      }
    },
  },

  methods: {
    initializeTooltips() {
      this.$nextTick(() => {
        if (window.$ && window.$.fn.tooltip) {
          window.$('[data-toggle="tooltip"]').tooltip();
        } else {
          document.querySelectorAll('[data-toggle="tooltip"]').forEach(element => {
            if (!element.hasAttribute('title')) {
              element.setAttribute('title', element.getAttribute('data-original-title') || '');
            }
          });
        }
      });
    },

    async checkMarketplaceToken() {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token') || urlParams.get('x-amzn-marketplace-token');
      const sku = urlParams.get('product_id');
      const account_id = urlParams.get('account_id');
      const loggedIn = await this.checkLoggedIn();
      this.type = 'aqfer';
      if (token) {
        this.formData.mkt_token = token;
        this.formData.sku = sku;
        sessionStorage.setItem('x-amzn-marketplace-token', token);
        sessionStorage.setItem('product_id', sku);
        sessionStorage.setItem('account_id', account_id);
        this.cleanUrlParameter();
        this.editable = false;
        this.errorMessage = '';
        this.type = 'aws';

      } else {
        const storedToken = sessionStorage.getItem('x-amzn-marketplace-token');
        const storedSku = sessionStorage.getItem('product_id');
        if (storedToken && storedSku) {
          this.formData.mkt_token = storedToken;
          this.formData.sku = storedSku;
          this.errorMessage = '';
          this.editable = false;
          this.type = 'aws';
        } else if (!loggedIn) {
          this.type = 'aws';
          this.errorMessage = 'Missing required marketplace token. Please ensure you are accessing this page through the AWS Marketplace.';
        }
      }
    },
    cleanUrlParameter() {
      const url = new URL(window.location);
      url.searchParams.delete('token');
      url.searchParams.delete('x-amzn-marketplace-token');
      url.searchParams.delete('product_id');
      url.searchParams.delete('account_id');
      window.history.replaceState({}, document.title, url.pathname + url.search);
    },

    async nextStep() {
      let valid = true;
      if (this.currentStep === 1 && this.existCust && this.cid) {
        valid = await this.validateCustomerID();
      }
      if (valid && this.canProceedToNextStep && this.currentStep <= 1 && this.errorMessage === '') {
        this.currentStep++;
        this.errorMessage = '';
        this.initializeTooltips();
      }
    },

    async handleStep2Next() {
      this.errorMessage = '';

      // Validate marketplace product ID if applicable
      if (this.formData.mkt_token) {
        const valid = await this.validateProductID();
        if (!valid || this.errorMessage) return;
      }

      // Ensure SKU registration status is fetched for existing customers
      if (this.existCust && this.cid && this.formData.sku && !this.registeredSkuStatus && !this.checkingSkuStatus) {
        await this.checkRegisteredSku();
      }

      if (this.errorMessage) return;

      // CID already created or API already called — just advance to CFM step
      if (this.createdCid || this.existingCidOnboardingCalled || this.isResumingFlow || this.isInProgress || this.isClientInputReceived) {
        this.currentStep++;
        this.initializeTooltips();
        return;
      }

      // New customer + CFM: call onboarding API to create CID, then show CFM step
      if (this.hasCfm && !this.existCust) {
        await this.createCid();
        if (!this.errorMessage) {
          this.currentStep++;
          this.initializeTooltips();
        }
        return;
      }

      // Existing customer + CFM: call onboarding API, then show CFM step
      if (this.hasCfm && this.existCust) {
        await this.callOnboardingForExistingCid();
        if (!this.errorMessage) {
          this.currentStep++;
          this.initializeTooltips();
        }
        return;
      }

      // Fallback: advance (e.g. CFM check not yet resolved)
      this.currentStep++;
      this.initializeTooltips();
    },

    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
        this.errorMessage = '';
        this.initializeTooltips();
      }
    },

    // Optional SKU fields left blank must be sent as null, not "" — the backend
    // rejects an empty string for fields like account_id ("invalid account_id").
    sanitizeSkuInfo() {
      const sanitized = {};
      Object.entries(this.formData.sku_info || {}).forEach(([key, val]) => {
        sanitized[key] = (val === '' || val === undefined) ? null : val;
      });
      return sanitized;
    },

    async submitForm() {
      this.isSubmitting = true;
      this.errorMessage = '';

      try {
        const submissionData = {
          email: this.formData.email,
          organization: this.orgName,
          cid: this.existCust ? this.cid : null,
          mkt_token: this.formData.mkt_token || null,
          sku: this.formData.sku,
          ...this.sanitizeSkuInfo()
        };

        const endpoint = this.type === 'aws'
          ? `${this.$store.state.clientMgmtUrl}/v1/marketplace/customer/onboarding`
          : `${this.$store.state.clientMgmtUrl}/v1/customer/onboarding`;

        const response = await axios.post(endpoint, submissionData, {
          headers: { 'Content-Type': 'application/json', ...this.getHeaders() }
        });

        // Use CID from response (new customer) or the known existing CID
        const cidToUse = response.data?.cid || response.data?.id || this.cid;

        // Trigger the onboarding process for the registered SKU
        await axios.post(
          `${this.$store.state.clientMgmtUrl}/v1/clients/${cidToUse}/registered_skus/${this.formData.sku}/onboarding`,
          {},
          { headers: { 'Content-Type': 'application/json', ...this.getHeaders() } }
        );

        this.isSubmitted = true;
        sessionStorage.clear();
        this.currentStep++;
        await this.sendWelcomeEmail(cidToUse);
      } catch (error) {
        this.errorMessage = error.response?.data?.error || error.message || 'An error occurred during onboarding. Please try again.';
      } finally {
        this.isSubmitting = false;
        sessionStorage.clear();
      }
    },

    // Called when "Create CID" is clicked for a no-CFM SKU — creates the CID only,
    // then advances to the confirmation step where onboarding is started separately.
    async createCidNoCfm() {
      if (this.createdCid) {
        this.currentStep++;
        this.initializeTooltips();
        return;
      }

      this.isCreatingCid = true;
      this.errorMessage = '';
      try {
        const submissionData = {
          email: this.formData.email,
          organization: this.orgName,
          cid: this.existCust ? this.cid : null,
          mkt_token: this.formData.mkt_token || null,
          sku: this.formData.sku,
          ...this.sanitizeSkuInfo()
        };

        const endpoint = this.type === 'aws'
          ? `${this.$store.state.clientMgmtUrl}/v1/marketplace/customer/onboarding`
          : `${this.$store.state.clientMgmtUrl}/v1/customer/onboarding`;

        const response = await axios.post(endpoint, submissionData, {
          headers: { 'Content-Type': 'application/json', ...this.getHeaders() }
        });

        this.createdCid = response.data?.cid || response.data?.id || this.cid;
        this.currentStep++;
        this.initializeTooltips();
      } catch (error) {
        this.errorMessage = error.response?.data?.error || error.message || 'Failed to create CID. Please try again.';
      } finally {
        this.isCreatingCid = false;
      }
    },

    // Called when "Create CID" is clicked for a SKU without an automated product
    // (is_automation_enabled === false) — creates the CID, sends the welcome email,
    // and advances to the confirmation step. No start-onboarding call is made.
    async createCidNoProduct() {
      if (this.createdCid) {
        this.currentStep++;
        this.initializeTooltips();
        return;
      }

      this.isCreatingCid = true;
      this.errorMessage = '';
      try {
        const submissionData = {
          email: this.formData.email,
          organization: this.orgName,
          cid: this.existCust ? this.cid : null,
          mkt_token: this.formData.mkt_token || null,
          sku: this.formData.sku,
          ...this.sanitizeSkuInfo()
        };

        const endpoint = this.type === 'aws'
          ? `${this.$store.state.clientMgmtUrl}/v1/marketplace/customer/onboarding`
          : `${this.$store.state.clientMgmtUrl}/v1/customer/onboarding`;

        const response = await axios.post(endpoint, submissionData, {
          headers: { 'Content-Type': 'application/json', ...this.getHeaders() }
        });

        this.createdCid = response.data?.cid || response.data?.id || this.cid;
        await this.sendWelcomeEmail(this.createdCid);
        this.currentStep++;
        this.initializeTooltips();
      } catch (error) {
        this.errorMessage = error.response?.data?.error || error.message || 'Failed to create CID. Please try again.';
      } finally {
        this.isCreatingCid = false;
      }
    },

    // Called when "Start Onboarding" is clicked on the confirmation step for a no-CFM SKU
    async startOnboardingNoCfm() {
      this.isSubmitting = true;
      this.errorMessage = '';
      try {
        const cidToUse = this.createdCid || this.cid;

        await axios.post(
          `${this.$store.state.clientMgmtUrl}/v1/clients/${cidToUse}/registered_skus/${this.formData.sku}/onboarding`,
          {},
          { headers: { 'Content-Type': 'application/json', ...this.getHeaders() } }
        );

        await this.sendWelcomeEmail(cidToUse);

        this.isSubmitted = true;
        sessionStorage.clear();
      } catch (error) {
        this.errorMessage = error.response?.data?.error || error.message || 'An error occurred during onboarding. Please try again.';
      } finally {
        this.isSubmitting = false;
        sessionStorage.clear();
      }
    },

    // Called when "Create CID" is clicked (new registration with CFM)
    async createCid() {
      this.isCreatingCid = true;
      this.errorMessage = '';
      try {
        const submissionData = {
          email: this.formData.email,
          organization: this.orgName,
          mkt_token: this.formData.mkt_token || null,
          sku: this.formData.sku,
          ...this.sanitizeSkuInfo()
        };

        const endpoint = this.type === 'aws'
          ? `${this.$store.state.clientMgmtUrl}/v1/marketplace/customer/onboarding`
          : `${this.$store.state.clientMgmtUrl}/v1/customer/onboarding`;

        const response = await axios.post(endpoint, submissionData, {
          headers: { 'Content-Type': 'application/json', ...this.getHeaders() }
        });

        this.createdCid = response.data?.cid || response.data?.id || '';

        // Re-fetch CFM passing the new CID so the backend can generate a CID-specific template
        const region = this.formData.sku_info?.region || null;
        await this.fetchCFM(region, this.createdCid);
        await this.sendWelcomeEmail(this.createdCid);
      } catch (error) {
        console.log("error creating CID:", error.response);
        this.errorMessage = error.response?.data?.error || error.message || 'Failed to create CID. Please try again.';
      } finally {
        this.isCreatingCid = false;
      }
    },

    // Called when "Start Onboarding" is clicked after CFM output is provided
    async startOnboardingWithCfm() {
      this.cfmOutputError = '';
      if (!this.cfmOutput || !this.cfmOutput.trim()) {
        this.cfmOutputError = 'CloudFormation output is required.';
        return;
      }

      // Build CFM output array from fill-mode values or from pasted JSON
      let cfmOutputData;
      if (this.cfmInputMode === 'fill' && this.cfmOutputFields.length > 0) {
        cfmOutputData = this.cfmOutputFields.map(f => ({
          OutputKey: f.key,
          OutputValue: String(this.cfmOutputFieldValues[f.key] || '').trim(),
          Description: f.description || ''
        })).filter(item => item.OutputValue);
      } else {
        try {
          cfmOutputData = JSON.parse(this.cfmOutput);
        } catch {
          this.cfmOutputError = 'Invalid JSON. Please check the CloudFormation output format.';
          return;
        }
      }

      this.isSubmitting = true;
      this.errorMessage = '';

      try {
        const cidToUse = this.createdCid || this.cid;

        // Save CFM output — validation errors surface here
        try {
          await axios.put(
            `${this.$store.state.clientMgmtUrl}/v1/clients/${cidToUse}/registered_skus/${this.formData.sku}/cfm_output`,
            cfmOutputData,
            { headers: { 'Content-Type': 'application/json', ...this.getHeaders() } }
          );
        } catch (putError) {
          const raw = putError.response?.data?.error || putError.message || 'Invalid CFM output.';
          // Format "invalid input: 0: X is required\n1: Y is required" into readable lines
          this.cfmOutputError = raw.replace(/^invalid input:\s*/i, '').trim();
          return;
        }

        // Trigger the onboarding process now that CFM output is saved
        await axios.post(
          `${this.$store.state.clientMgmtUrl}/v1/clients/${cidToUse}/registered_skus/${this.formData.sku}/onboarding`,
          {},
          { headers: { 'Content-Type': 'application/json', ...this.getHeaders() } }
        );

        this.isSubmitted = true;
        sessionStorage.clear();
        this.currentStep++;

      } catch (error) {
        this.errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'An error occurred during onboarding. Please try again.';
      } finally {
        this.isSubmitting = false;
      }
    },

    // Checks if the existing customer's selected SKU is already registered and its status
    async checkRegisteredSku() {
      if (!this.cid || !this.formData.sku) return;
      this.checkingSkuStatus = true;
      try {
        const response = await axios.get(
          `${this.$store.state.clientMgmtUrl}/v1/clients/${this.cid}/registered_skus/${this.formData.sku}`,
          { headers: this.getHeaders() }
        );
        this.registeredSkuData = response.data;
        this.registeredSkuStatus = response.data?.status || null;

        // Pre-populate sku_info from the registered data for the resume flow
        if (this.registeredSkuStatus === 'AWAITING_CLIENT_INPUT' && response.data) {
          const { status, registration_time, last_updated_at, onboarding_ticket, sku, ...skuInfo } = response.data;
          this.formData.sku_info = { ...this.formData.sku_info, ...skuInfo };
          // Fetch CFM template with the existing CID so it can be shown in step 3
          const region = response.data.region || null;
          if (region) {
            this.fetchCFM(region, this.cid);
          }
        }
      } catch (error) {
        // 404 = SKU not yet registered for this CID, proceed with normal flow
        if (error.response?.status !== 404) {
          this.errorMessage = error.response?.data?.message || 'Failed to check SKU registration status.';
        }
      } finally {
        this.checkingSkuStatus = false;
      }
    },

    // Fetch the required CFM output fields for the selected SKU
    async fetchCfmOutputFields() {
      if (!this.formData.sku) return;
      this.cfmFieldsLoading = true;
      try {
        const res = await axios.get(
          `${this.$store.state.clientMgmtUrl}/v1/skus/${this.formData.sku}/cfm_output_fields`,
          { headers: this.getHeaders() }
        );
        const raw = Array.isArray(res.data?.output_fields)
          ? res.data.output_fields
          : Array.isArray(res.data) ? res.data : [];
        // Normalise: API returns {name, description}; also accept {key, OutputKey} variants
        this.cfmOutputFields = raw.map(f => ({
          key: f.name || f.key || f.OutputKey || f,
          description: f.description || f.Description || '',
          required: f.required !== false
        }));
        // Initialise value map (preserve any existing values)
        const vals = {};
        this.cfmOutputFields.forEach(f => { vals[f.key] = this.cfmOutputFieldValues[f.key] || ''; });
        this.cfmOutputFieldValues = vals;
        // Default to fill mode when fields are available
        if (this.cfmOutputFields.length > 0) this.cfmInputMode = 'fill';
      } catch {
        this.cfmOutputFields = [];
        this.cfmInputMode = 'paste';
      } finally {
        this.cfmFieldsLoading = false;
      }
    },

    // Fill mode → update cfmOutput (JSON preview)
    syncFieldsToJson() {
      const arr = this.cfmOutputFields.map(f => ({
        OutputKey: f.key,
        OutputValue: String(this.cfmOutputFieldValues[f.key] || '').trim(),
        Description: f.description || ''
      })).filter(item => item.OutputValue);
      this.cfmOutput = arr.length ? JSON.stringify(arr, null, 2) : '';
    },

    // Paste mode → populate fill fields when valid JSON is detected
    syncJsonToFields() {
      if (!this.cfmOutput?.trim()) return;
      try {
        const parsed = JSON.parse(this.cfmOutput);
        if (Array.isArray(parsed)) {
          const updated = { ...this.cfmOutputFieldValues };
          parsed.forEach(item => {
            if (item.OutputKey) updated[item.OutputKey] = item.OutputValue || '';
          });
          this.cfmOutputFieldValues = updated;
        }
      } catch { /* keep existing field values */ }
    },

    async validateCustomerID() {
      if (!this.cid) return;
      try {
        const response = await axios.get(`${this.$store.state.clientMgmtUrl}/v1/cids/${this.cid}?organization=${this.orgName}`, {
          headers: this.getHeaders(),
        })
        if (response.data) {
          this.errorMessage = '';
        } else {
          this.errorMessage = response.message || 'Invalid Customer ID';
        }
        return response.data;
      } catch (error) {
        this.errorMessage = 'Invalid Customer ID or Organization Name. Please try again.';
      }
    },
    async validateProductID() {
      if (!this.formData.sku) return;
      try {
        const response = await axios.get(`${this.$store.state.clientMgmtUrl}/v1/marketplace/products?pid=${this.formData.sku}`, {
          headers: this.getHeaders(),
        })
        if (response.data && response.data.length > 0) {
          this.errorMessage = '';
        } else {
          this.errorMessage = response.message || 'Invalid Product ID';
        }
        return response.data;
      } catch (error) {
        this.errorMessage = error.response.data.message || 'Error validating Product ID. Please try again.';
      }
    },
    async checkLoggedIn() {
      return this.$store.dispatch('whoAmICheck').then(res => {
        if (res && res.data?.email) {
          return true;
        }
        return false;
      }).catch(() => {
        return false;
      })
    },
    getHeaders() {
      const headers = {};
      const accesstoken = this.$store.state.accessToken;
      const token = this.formData.mkt_token || sessionStorage.getItem('x-amzn-marketplace-token');
      if (token) {
        headers['mkt_token'] = `${token}`;
      } else if (accesstoken) {
        headers['Authorization'] = `Bearer ${accesstoken}`;
      }
      return headers;
    },
    async fetchSKUList(){
       try {
        await axios.get(`${this.$store.state.clientMgmtUrl}/v1/skus?marketplace=${this.type}`, {
            headers: {
              ...this.getHeaders()
            }
          }).then((res) => {
            this.skuList = res?.data || []
          })

      } catch (error) {
        this.errorMessage = error.message || 'An error occurred during onboarding. Please try again.';
      }
    },
    debouncedFetchCFM: debounce(function(region) {
      this.fetchCFM(region);
    }, 500),
    async fetchCFM(region, cid = null){
      this.templateLoading = true;
       try {
        let payload = {
          name: this.formData.sku
        }
        if (region)
          payload.parameters = { region }
        if (cid)
          payload.cid = cid
        await axios.post(`${this.$store.state.clientMgmtUrl}/v1/onboarding/cfm`, payload, {
            headers: {
              'Content-Type': 'application/json', ...this.getHeaders()
            }
          }).then((res) => {
            this.templateUrl = res.data.template
            // Mark CFM check as complete (SKU has a CFM)
            if (!region || cid) this.cfmCheckComplete = true
          })

      } catch (error) {
        const errMsg = error.response?.data?.error || '';
        const noTemplate = error.response?.status === 404 ||
          errMsg.includes('invalid input') ||
          errMsg.includes('record not found') ||
          errMsg.includes('CFM not found');
        if (noTemplate) {
          this.noTemplate = true;
          this.cfmCheckComplete = true;
        } else {
          this.errorMessage = 'Unable to fetch CFM template. Please try again or contact support.';
        }
      } finally{
          this.templateLoading = false
      }
    },
    getOptions(val){
      return val.map(item=>{ return {label:item,value:item}})
    },
    // SKU documentation links on Helpjuice, as returned by the onboarding SKU list.
    helpjuiceLinksForSku(sku) {
      const links = this.skuList.find(item => item.sku === sku)?.helpjuice_urls;
      return Array.isArray(links) ? links.filter(Boolean) : [];
    },
    async sendWelcomeEmail(cid) {
      const sku = this.formData.sku;
      const helpjuiceLinks = this.helpjuiceLinksForSku(sku);
      const cfmSection = (!this.noTemplate && this.templateUrl)
        ? `- CloudFormation Template: ${this.templateUrl}`
        : '';
      try {
        await axios.post(
          `${this.$store.state.clientMgmtUrl}/v1/cids/${cid}/notify/email`,
          {
            template: 'customer_sku_welcome',
            fields: {
              sku,
              organization: this.orgName,
              helpjuice_link: helpjuiceLinks.join(', '),
              cfm_section: cfmSection,
            }
          },
          { headers: { 'Content-Type': 'application/json', ...this.getHeaders() } }
        );
      } catch (error) {
        console.warn('Failed to send welcome email:', error.message);
      }
    },
    // Existing customer + CFM: call onboarding API first, then show CFM output section 
    async callOnboardingForExistingCid() {
      this.isCreatingCid = true;
      this.errorMessage = '';
      try {
        const submissionData = {
          email: this.formData.email,
          organization: this.orgName,
          cid: this.cid,
          mkt_token: this.formData.mkt_token || null,
          sku: this.formData.sku,
          ...this.sanitizeSkuInfo()
        };
        const endpoint = this.type === 'aws'
          ? `${this.$store.state.clientMgmtUrl}/v1/marketplace/customer/onboarding`
          : `${this.$store.state.clientMgmtUrl}/v1/customer/onboarding`;
        await axios.post(endpoint, submissionData, {
          headers: { 'Content-Type': 'application/json', ...this.getHeaders() }
        });
        this.existingCidOnboardingCalled = true;
        await this.sendWelcomeEmail(this.cid);
      } catch (error) {
        this.errorMessage = error.response?.data?.error || error.message || 'Failed to start onboarding. Please try again.';
      } finally {
        this.isCreatingCid = false;
      }
    },

    // CLIENT_INPUT_RECEIVED: re-trigger onboarding to kick off codebuild
    async retriggerOnboarding() {
      this.isCreatingCid = true;
      this.errorMessage = '';
      try {
        await axios.post(
          `${this.$store.state.clientMgmtUrl}/v1/clients/${this.cid}/registered_skus/${this.formData.sku}/onboarding`,
          {},
          { headers: { 'Content-Type': 'application/json', ...this.getHeaders() } }
        );
        this.isSubmitted = true;
        sessionStorage.clear();
        this.currentStep++;
      } catch (error) {
        this.errorMessage = error.response?.data?.error || error.message || 'Failed to trigger onboarding. Please try again.';
      } finally {
        this.isCreatingCid = false;
      }
    },

    async copyExistingCid() {
      try {
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(this.cid);
        } else {
          const input = document.createElement('textarea');
          input.style = 'position: absolute; left: -1000px; top: -1000px';
          input.value = this.cid;
          document.body.appendChild(input);
          input.select();
          document.execCommand('copy');
          document.body.removeChild(input);
        }
        this.$store.dispatch('addToast', { message: 'CID copied to clipboard.', type: 'info' });
      } catch { /* silently fail */ }
    },

    async copyCreatedCid() {
      try {
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(this.createdCid);
        } else {
          const input = document.createElement('textarea');
          input.style = 'position: absolute; left: -1000px; top: -1000px';
          input.value = this.createdCid;
          document.body.appendChild(input);
          input.select();
          document.execCommand('copy');
          document.body.removeChild(input);
        }
        this.$store.dispatch('addToast', { message: 'CID copied to clipboard.', type: 'info' });
      } catch {
        // silently fail
      }
    },
  }
};
</script>

<style lang="scss" scoped>
@use '../theme/scss/user-variables' as *;

// ── Spinner ──────────────────────────────────────────────────────────────────
.spin { animation: spin 1s linear infinite; }
@keyframes spin {
  to { transform: rotate(360deg); }
}

// ── Page layout — exact viewport height, no scroll ───────────────────────────
.onboarding-page {
  height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

// Make Bootstrap container + row fill the remaining height
.onboarding-container {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding-top: 0;
  padding-bottom: 0;
}

.onboarding-row {
  flex: 1;
  min-height: 0;
  margin: 0;
}

// Form column — centers content, scrolls internally only if content overflows
.onboarding-form-col {
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow-y: auto;
  padding-top: 1.5rem;
  padding-bottom: 1.5rem;
}

// Logo column — centers logo in the remaining height
.onboarding-logo-col {
  align-items: center;
  justify-content: center;
  border-left: 1px solid rgba(0, 0, 0, 0.05);

  body.dark-mode & {
    border-left-color: rgba(255, 255, 255, 0.05);
  }
}

// ── Step wizard ──────────────────────────────────────────────────────────────
.step-content { /* no fixed min-height — let content determine size */ }

.step-circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: #e9ecef;
  border: 2px solid #dee2e6;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  font-weight: 700;
  font-size: 0.82rem;
  transition: background-color 0.25s, border-color 0.25s, color 0.25s, box-shadow 0.25s;

  &.active {
    background-color: #2c7be5;
    border-color: #2c7be5;
    color: #fff;
    box-shadow: 0 0 0 4px rgba(44, 123, 229, 0.18);
  }
  &.completed {
    background-color: #00a854;
    border-color: #00a854;
    color: #fff;
  }
}

.step-line {
  width: 48px;
  height: 2px;
  background-color: #dee2e6;
  margin: 0 6px;
  transition: background-color 0.3s;
  &.active { background-color: #00a854; }
}

// ── SKU badge (replaces alert-light) ─────────────────────────────────────────
.sku-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.75rem;
  border-radius: 2rem;
  background: #eef4ff;
  border: 1px solid #c5d9f8;
  color: #1a56db;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.02em;

  body.dark-mode & {
    background: rgba(44, 123, 229, 0.15);
    border-color: rgba(44, 123, 229, 0.35);
    color: #7eb5ff;
  }
}

// ── Resume banner ─────────────────────────────────────────────────────────────
.resume-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  padding: 0.9rem 1rem;
  border-radius: 0.5rem;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-left: 4px solid #f59e0b;

  .resume-banner-icon {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #fef3c7;
    border: 1px solid #fcd34d;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #d97706;
    font-size: 0.9rem;
  }

  .resume-banner-body {
    flex: 1;
    color: #92400e;
    font-size: 0.875rem;
    code {
      background: rgba(0,0,0,0.06);
      padding: 0.1em 0.35em;
      border-radius: 3px;
      font-size: 0.85em;
      color: #7c3400;
    }
  }

  body.dark-mode & {
    background: rgba(245, 158, 11, 0.08);
    border-color: rgba(245, 158, 11, 0.3);
    border-left-color: #f59e0b;
    .resume-banner-icon { background: rgba(245,158,11,0.15); border-color: rgba(245,158,11,0.3); }
    .resume-banner-body { color: #fcd34d; }
    .resume-banner-body code { background: rgba(255,255,255,0.08); color: #fde68a; }
  }
}

// ── IN_PROGRESS banner ────────────────────────────────────────────────────────
.in-progress-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  padding: 0.9rem 1rem;
  border-radius: 0.5rem;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-left: 4px solid #10b981;

  .in-progress-banner-icon {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #d1fae5;
    border: 1px solid #6ee7b7;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #059669;
    font-size: 0.9rem;
  }

  .in-progress-banner-body {
    flex: 1;
    color: #065f46;
    font-size: 0.875rem;
    code {
      background: rgba(0,0,0,0.06);
      padding: 0.1em 0.35em;
      border-radius: 3px;
      font-size: 0.85em;
      color: #047857;
    }
  }

  body.dark-mode & {
    background: rgba(16, 185, 129, 0.07);
    border-color: rgba(16, 185, 129, 0.25);
    border-left-color: #10b981;
    .in-progress-banner-icon { background: rgba(16,185,129,0.15); border-color: rgba(16,185,129,0.3); color: #34d399; }
    .in-progress-banner-body { color: #6ee7b7; code { background: rgba(255,255,255,0.08); color: #a7f3d0; } }
  }
}

// ── CLIENT_INPUT_RECEIVED banner ──────────────────────────────────────────────
.cir-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  padding: 0.9rem 1rem;
  border-radius: 0.5rem;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-left: 4px solid #3b82f6;

  .cir-banner-icon {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #dbeafe;
    border: 1px solid #93c5fd;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #2563eb;
    font-size: 0.9rem;
  }

  .cir-banner-body {
    flex: 1;
    color: #1e40af;
    font-size: 0.875rem;
  }

  body.dark-mode & {
    background: rgba(59, 130, 246, 0.08);
    border-color: rgba(59, 130, 246, 0.25);
    border-left-color: #3b82f6;
    .cir-banner-icon { background: rgba(59,130,246,0.15); border-color: rgba(59,130,246,0.3); color: #60a5fa; }
    .cir-banner-body { color: #93c5fd; }
  }
}

// ── CID created compact note ──────────────────────────────────────────────────
.cid-note {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.45rem 0.75rem;
  background: #f0fdf6;
  border: 1px solid #bbf7d0;
  border-radius: 0.4rem;
  font-size: 0.82rem;

  .cid-note-icon {
    color: #059669;
    font-size: 0.95rem;
    flex-shrink: 0;
  }

  .cid-note-label {
    color: #059669;
    font-weight: 600;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .cid-note-value {
    flex: 1;
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 0.88rem;
    font-weight: 700;
    color: #065f46;
    background: transparent;
    letter-spacing: 0.03em;
  }

  .cid-note-copy {
    flex-shrink: 0;
    padding: 0.18rem 0.5rem;
    font-size: 0.73rem;
    font-weight: 500;
    background: #fff;
    border: 1px solid #6ee7b7;
    color: #065f46;
    border-radius: 0.3rem;
    transition: background 0.15s, border-color 0.15s;
    &:hover { background: #d1fae5; border-color: #34d399; }
  }

  body.dark-mode & {
    background: rgba(5, 150, 105, 0.08);
    border-color: rgba(52, 211, 153, 0.25);
    .cid-note-icon { color: #34d399; }
    .cid-note-label { color: #34d399; }
    .cid-note-value { color: #a7f3d0; }
    .cid-note-copy {
      background: rgba(0,168,84,0.12);
      border-color: rgba(110,231,183,0.35);
      color: #6ee7b7;
      &:hover { background: rgba(0,168,84,0.22); }
    }
  }
}


// ── CFM template action buttons ───────────────────────────────────────────────
.cfm-template-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.btn-xs {
  font-size: 0.76rem;
  padding: 0.22rem 0.6rem;
  line-height: 1.4;
  border-radius: 0.3rem;
  font-weight: 500;
}

// ── No automation / no CFM info banner ────────────────────────────────────────
.info-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  padding: 0.9rem 1rem;
  border-radius: 0.5rem;
  background: #f8f9fa;
  border: 1px solid #e2e5e9;
  border-left: 4px solid #9aa5b1;

  .info-banner-icon {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #eef0f2;
    border: 1px solid #d7dbe0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6c757d;
    font-size: 0.9rem;
  }

  .info-banner-body {
    flex: 1;
    color: #495057;
    font-size: 0.875rem;

    .sku-chip {
      display: inline-block;
      white-space: nowrap;
      font-weight: 600;
      background: rgba(0,0,0,0.06);
      padding: 0.05em 0.45em;
      border-radius: 4px;
      font-size: 0.92em;
      color: #343a40;
    }
  }

  body.dark-mode & {
    background: rgba(255,255,255,0.04);
    border-color: rgba(255,255,255,0.12);
    border-left-color: #6b7280;
    .info-banner-icon { background: rgba(255,255,255,0.06); border-color: rgba(255,255,255,0.15); color: #9ca3af; }
    .info-banner-body {
      color: #cbd5e1;
      .sku-chip { background: rgba(255,255,255,0.1); color: #e5e7eb; }
    }
  }
}

// ── CFM output card ───────────────────────────────────────────────────────────
.cfm-output-card {
  border: 1px solid #e2e8f0;
  border-radius: 0.55rem;
  overflow: hidden;

  .cfm-output-card-header {
    display: flex;
    align-items: center;
    padding: 0.6rem 0.9rem;
    background: #f1f5f9;
    border-bottom: 1px solid #e2e8f0;
    font-size: 0.82rem;
    font-weight: 600;
    color: #334155;

    .cfm-output-hint {
      font-size: 0.73rem;
      font-weight: 400;
      color: #94a3b8;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    body.dark-mode & {
      background: rgba(255,255,255,0.05);
      border-bottom-color: rgba(255,255,255,0.1);
      color: #cbd5e1;
      .cfm-output-hint { color: #64748b; }
    }
  }

  .cfm-output-card-body {
    background: #fff;
    body.dark-mode & { background: #1a1f2e; }
  }

  &:focus-within {
    border-color: #60a5fa;
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.15);
  }

  body.dark-mode & {
    border-color: rgba(255,255,255,0.1);
    &:focus-within { border-color: rgba(96,165,250,0.5); }
  }
}

.cfm-output-textarea {
  display: block;
  width: 100%;
  padding: 0.75rem;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 0.8rem;
  line-height: 1.6;
  color: #1e293b;
  background: transparent;
  border: none;
  resize: vertical;
  outline: none;
  min-height: 180px;

  &::placeholder { color: #94a3b8; }

  &.has-error { background: #fff5f5; }

  body.dark-mode & {
    color: #e2e8f0;
    &::placeholder { color: #475569; }
    &.has-error { background: rgba(220, 53, 69, 0.08); }
  }
}

.cfm-output-error {
  padding: 0.4rem 0.9rem;
  font-size: 0.78rem;
  color: #dc3545;
  background: #fff5f5;
  border-top: 1px solid #fecaca;
  display: flex;
  align-items: flex-start;

  body.dark-mode & {
    background: rgba(220,53,69,0.1);
    border-top-color: rgba(220,53,69,0.2);
    color: #f87171;
  }
}

.cfm-output-valid {
  padding: 0.4rem 0.9rem;
  font-size: 0.78rem;
  color: #059669;
  background: #f0fdf4;
  border-top: 1px solid #bbf7d0;
  display: flex;
  align-items: center;

  body.dark-mode & {
    background: rgba(5,150,105,0.08);
    border-top-color: rgba(52,211,153,0.2);
    color: #34d399;
  }
}

// ── CFM fill-mode toggle (Fill Fields / Paste JSON) ──────────────────────────
.cfm-mode-toggle {
  display: flex;
  align-items: center;
  gap: 2px;
  background: rgba(0,0,0,0.06);
  border-radius: 0.35rem;
  padding: 2px;
  flex-shrink: 0;

  body.dark-mode & { background: rgba(255,255,255,0.08); }
}

.cfm-mode-btn {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.55rem;
  font-size: 0.72rem;
  font-weight: 500;
  border: none;
  border-radius: 0.25rem;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;

  &:hover { background: rgba(0,0,0,0.06); color: #334155; }

  &.active {
    background: #fff;
    color: #2563eb;
    font-weight: 600;
    box-shadow: 0 1px 3px rgba(0,0,0,0.12);
  }

  body.dark-mode & {
    color: #94a3b8;
    &:hover { background: rgba(255,255,255,0.06); color: #cbd5e1; }
    &.active {
      background: rgba(255,255,255,0.1);
      color: #60a5fa;
      box-shadow: 0 1px 3px rgba(0,0,0,0.3);
    }
  }
}

// ── CFM fill-mode body ────────────────────────────────────────────────────────
.cfm-fill-body {
  background: #fff;
  padding: 0.5rem 0.75rem 0;

  body.dark-mode & { background: #1a1f2e; }
}

.cfm-field-row {
  display: grid;
  grid-template-columns: 1fr auto 1.4rem;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0;
  border-bottom: 1px solid #f1f5f9;

  &:last-of-type { border-bottom: none; }

  &.cfm-field-missing .cfm-field-input {
    border-color: #fca5a5;
    background: #fff5f5;

    body.dark-mode & { border-color: rgba(248,113,113,0.5); background: rgba(220,53,69,0.07); }
  }

  body.dark-mode & { border-bottom-color: rgba(255,255,255,0.06); }
}

.cfm-field-label {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;

  > span:first-child {
    font-size: 0.8rem;
    font-weight: 600;
    color: #334155;
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    body.dark-mode & { color: #cbd5e1; }
  }
}

.cfm-field-desc {
  font-size: 0.7rem;
  color: #94a3b8;
  font-family: inherit;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  body.dark-mode & { color: #64748b; }
}

.cfm-field-input {
  width: 200px;
  min-width: 120px;
  padding: 0.3rem 0.55rem;
  font-size: 0.8rem;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  border: 1px solid #e2e8f0;
  border-radius: 0.3rem;
  background: #f8fafc;
  color: #1e293b;
  transition: border-color 0.15s, box-shadow 0.15s;
  outline: none;

  &:focus {
    border-color: #60a5fa;
    box-shadow: 0 0 0 2px rgba(96,165,250,0.2);
    background: #fff;
  }

  &::placeholder { color: #c0ccd8; }

  body.dark-mode & {
    background: rgba(255,255,255,0.05);
    border-color: rgba(255,255,255,0.1);
    color: #e2e8f0;
    &:focus { border-color: rgba(96,165,250,0.6); background: rgba(255,255,255,0.08); }
    &::placeholder { color: #475569; }
  }
}

.cfm-field-check {
  font-size: 0.95rem;
  color: #10b981;
  flex-shrink: 0;
  justify-self: center;

  body.dark-mode & { color: #34d399; }
}

.cfm-missing-bar {
  display: flex;
  align-items: center;
  padding: 0.4rem 0.75rem;
  font-size: 0.75rem;
  color: #dc3545;
  background: #fff5f5;
  border-top: 1px solid #fecaca;
  flex-wrap: wrap;
  gap: 0.2rem;

  body.dark-mode & {
    background: rgba(220,53,69,0.08);
    border-top-color: rgba(220,53,69,0.2);
    color: #f87171;
  }
}

// ── Instructions info box ─────────────────────────────────────────────────────
.alert-info-bg {
  background-color: #e8f1ff;
  border-color: #bfcfe8;
  border: 1px solid #bfcfe8;
  border-radius: 0.4rem;
  padding: 0.85rem 1rem;
  color: #1e3a5f;

  body.dark-mode & {
    background-color: rgba(44, 123, 229, 0.08);
    border-color: rgba(44, 123, 229, 0.2);
    color: #93c5fd;
  }
}

// ── Form & layout ─────────────────────────────────────────────────────────────
.onboarding-form-col :deep(.form-group) {
  margin-bottom: 0.55rem !important;
}

.logo-img {
  max-width: 200px;
}

.sku-config-fields.two-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem 1rem;
  align-items: start;
}

.sku-config-fields.two-columns :deep(.form-group) {
  margin-bottom: 0.8rem !important;
}

.form-label i[data-toggle="tooltip"] {
  font-size: 0.875rem;
  cursor: help;
  opacity: 0.7;
  &:hover { opacity: 1; }
}

body.dark-mode {
  .alert-light {
    background-color: $dark-card-bg;
    border-color: $dark-border-color;
    color: $dark-text-primary;
  }
}
</style>
